import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      const to = location.state?.from?.pathname || "/dashboard";
      navigate(to, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="login-page">
      {/* Left panel */}
      <div className="bg-[var(--peak-accent)] text-white p-10 md:p-16 flex flex-col justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-black flex items-center justify-center font-heading font-black text-xl">P</div>
          <div className="font-heading font-black text-2xl tracking-tighter">PEAK CRM</div>
        </div>
        <div>
          <h1 className="font-heading font-black uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter">
            Every deal.<br/>Every contact.<br/>One command centre.
          </h1>
          <p className="mt-6 text-base md:text-lg font-medium max-w-md">
            Peak is a no-nonsense CRM built for teams who ship. Pipelines, calendar,
            funnels & an AI copilot — without the SaaS bloat.
          </p>
        </div>
        <div className="section-label text-white/80">© 2026 Peak Labs. All rights reserved.</div>
      </div>

      {/* Right form */}
      <div className="bg-white p-10 md:p-16 flex items-center">
        <form onSubmit={submit} className="w-full max-w-md space-y-6" data-testid="login-form">
          <div>
            <div className="section-label">Welcome back</div>
            <h2 className="font-heading font-black text-3xl md:text-4xl tracking-tighter mt-1">Sign in.</h2>
          </div>

          <div>
            <label className="section-label" htmlFor="email">Email</label>
            <input
              id="email"
              data-testid="login-email"
              className="brutalist-input"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div>
            <label className="section-label" htmlFor="password">Password</label>
            <input
              id="password"
              data-testid="login-password"
              className="brutalist-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            data-testid="login-submit"
            disabled={loading}
            className="w-full bg-[var(--peak-accent)] hover:bg-black text-white font-bold uppercase tracking-widest px-6 py-4 flex items-center justify-center gap-2 transition-colors disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-sm">
            No account?{" "}
            <Link to="/signup" data-testid="go-register" className="font-bold underline hover:text-[var(--peak-accent)]">
              Create one
            </Link>
            {" · "}
            <Link to="/forgot-password" data-testid="go-forgot" className="font-bold underline hover:text-[var(--peak-accent)]">
              Forgot password?
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
